import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Dimensions,
    FlatList,
    Platform,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from 'react-native';
import Slider from '@react-native-community/slider';
import { CreateReportModal, CreateReportSubmitInput, ReportProductDraft } from '../components/CreateReportModal';
import { FiltersModal } from '../components/FiltersModal';
import { OwnershipScopeToggle } from '../components/OwnershipScopeToggle';
import { PressableCard } from '../components/PressableCard';
import { ProductRow } from '../components/ProductRow';
import { ScreenTransition } from '../components/ScreenTransition';
import { SkeletonProductList } from '../components/SkeletonLoader';
import { getProductTypeName } from '../constants/productTypeNames';
import { COLORS } from '../constants/theme';
import { useFilterOptions } from '../hooks/useFilterOptions';
import { useProductsList, type Product } from '../hooks/useProductsList';
import {
  exportProductsListPdf,
  type ProductsListPdfColumn,
} from '../services/productsListPdfService';
import {
  apiService,
  CustomReportColleague,
  CustomReportDetail,
} from '../services/apiService';
import authService from '../services/authService';
import styles from './ProductsScreen.styles';

const { width } = Dimensions.get('window');

const formatCalendarChipDate = (isoDate: string): string => {
  const date = new Date(`${isoDate}T12:00:00`);
  if (Number.isNaN(date.getTime())) {
    return isoDate;
  }
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

interface ProductsScreenProps {
  onBackPress: () => void;
  onNavigateToProducts?: () => void;
  onNavigateToHome?: () => void;
  onProductPress?: (isin: string, fromCustomReport?: boolean) => void;
  onFiltersChange?: (
    filters: {
      currency?: string;
      issuer?: string;
      maturityQuarter?: string;
      underlying?: string;
      eventType?: string;
      weekStart?: string;
      eventDate?: string;
      eventBucket?: 'nextWeek' | 'nextMaturity';
      nearBarrier?: boolean;
      riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
      ownershipScope?: 'group' | 'self';
      maxDistancePct?: number;
      productTypes?: string[];
      issuers?: string[];
      reportIds?: string[];
      includeWarrants?: boolean;
    } | null
  ) => void;
  ownershipScope?: 'group' | 'self';
  onOwnershipScopeChange?: (value: 'group' | 'self') => void;
  showOwnershipScopeToggle?: boolean;
  initialFilters?: {
    currency?: string;
    issuer?: string;
    maturityQuarter?: string;
    underlying?: string;
    eventType?: string;
    weekStart?: string;
    eventDate?: string;
    eventBucket?: 'nextWeek' | 'nextMaturity';
    nearBarrier?: boolean;
    riskType?: 'barrier' | 'barrierHit' | 'lowStrike';
    ownershipScope?: 'group' | 'self';
    maxDistancePct?: number;
    productTypes?: string[];
    issuers?: string[];
    reportIds?: string[];
    selectedProductTypes?: string[];
    selectedIssuers?: string[];
    includeWarrants?: boolean;
  } | null;
}

export const ProductsScreen: React.FC<ProductsScreenProps> = ({
  onBackPress,
  onProductPress,
  onFiltersChange,
  ownershipScope: globalOwnershipScope,
  onOwnershipScopeChange,
  showOwnershipScopeToggle = true,
  initialFilters,
}) => {
  const normalizedInitialFilters = initialFilters
    ? {
        ...initialFilters,
        productTypes: initialFilters.productTypes ?? initialFilters.selectedProductTypes,
        issuers:
          initialFilters.issuers ??
          initialFilters.selectedIssuers ??
          (initialFilters.issuer ? [initialFilters.issuer] : undefined),
      }
    : null;

  // Inicializar los filtros seleccionados con los initialFilters si existen
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>(
    normalizedInitialFilters?.productTypes || []
  );
  const [selectedIssuers, setSelectedIssuers] = useState<string[]>(
    normalizedInitialFilters?.issuers || []
  );
  const [selectedReports, setSelectedReports] = useState<string[]>(
    (normalizedInitialFilters?.reportIds || []).slice(0, 1)
  );
  const [ownershipScope, setOwnershipScope] = useState<'group' | 'self'>(
    globalOwnershipScope || normalizedInitialFilters?.ownershipScope || 'group'
  );
  const [filtersModalVisible, setFiltersModalVisible] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [reportModalMode, setReportModalMode] = useState<'create' | 'edit'>('create');
  const [colleagues, setColleagues] = useState<CustomReportColleague[]>([]);
  const [loadingColleagues, setLoadingColleagues] = useState(false);
  const [loadingReportProducts, setLoadingReportProducts] = useState(false);
  const [reportModalProducts, setReportModalProducts] = useState<ReportProductDraft[]>([]);
  const [reportModalInitial, setReportModalInitial] = useState<CustomReportDetail | null>(null);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [activeReport, setActiveReport] = useState<CustomReportDetail | null>(null);
  const reportModalRequestIdRef = useRef(0);

  useEffect(() => {
    if (globalOwnershipScope) {
      setOwnershipScope(globalOwnershipScope);
    }
  }, [globalOwnershipScope]);

  const {
    searchQuery,
    products,
    loading,
    refreshing,
    error,
    dashboardFilters,
    setDashboardFilters,
    setSearchQuery,
    handleRefresh,
  } = useProductsList(normalizedInitialFilters);

  // Extraer filtros de los productos cargados
  const {
    productTypes,
    issuers,
    reports,
    loading: loadingFilterOptions,
    reloadReports,
  } = useFilterOptions(products);

  // Sincronizar selectedIssuers cuando cambia initialFilters (cuando viene desde la home)
  // Esperamos a que los filtros se carguen para poder seleccionar el issuer
  useEffect(() => {
    if (initialFilters?.issuer && !loadingFilterOptions && issuers.length > 0) {
      setSelectedIssuers(prev => {
        // Solo agregar si no está ya en la lista y si existe en los issuers disponibles
        const issuerExists = issuers.some(opt => opt.value === initialFilters.issuer);
        if (issuerExists && !prev.includes(initialFilters.issuer!)) {
          return [...prev, initialFilters.issuer!];
        }
        return prev;
      });

      // Limpiar el issuer del dashboardFilters para evitar duplicados
      // Ya que ahora lo manejamos con selectedIssuers
      setDashboardFilters(prev => {
        if (prev && prev.issuer === initialFilters.issuer) {
          const { issuer, ...rest } = prev;
          return Object.keys(rest).length > 0 ? rest : null;
        }
        return prev;
      });
    }
  }, [initialFilters?.issuer, loadingFilterOptions, issuers, setDashboardFilters]);

  // Actualizar filtros automáticamente cuando cambian las selecciones
  useEffect(() => {
    onOwnershipScopeChange?.(ownershipScope);
  }, [ownershipScope, onOwnershipScopeChange]);

  useEffect(() => {
    setDashboardFilters(prev => {
      const next = {
        ...(prev ?? {}),
        ownershipScope,
        productTypes: selectedProductTypes.length > 0 ? selectedProductTypes : undefined,
        issuers: selectedIssuers.length > 0 ? selectedIssuers : undefined,
      };

      const hasActive = Object.values(next).some(v => v !== undefined && v !== null);
      return hasActive ? next : null;
    });
  }, [ownershipScope, selectedProductTypes, selectedIssuers, setDashboardFilters]);

  // Sincronizar el estado de filtros con AppNavigator para preservarlo al navegar
  useEffect(() => {
    if (onFiltersChange) {
      const currentFilters = {
        ...dashboardFilters,
        ownershipScope,
        productTypes: selectedProductTypes.length > 0 ? selectedProductTypes : undefined,
        issuers: selectedIssuers.length > 0 ? selectedIssuers : undefined,
        reportIds: selectedReports.length > 0 ? selectedReports : undefined,
      };

      // Solo notificar si hay filtros activos
      const hasActiveFilters = Object.values(currentFilters).some(
        v => v !== undefined && v !== null
      );
      onFiltersChange(hasActiveFilters ? currentFilters : null);
    }
  }, [
    ownershipScope,
    selectedProductTypes,
    selectedIssuers,
    selectedReports,
    dashboardFilters,
    onFiltersChange,
  ]);

  // Formatear precio
  const formatPrice = (price: number): string => {
    if (!price || price === 0) return '-';
    return price.toFixed(2);
  };

  const abbreviateProductName = (name: string): string => {
    if (!name) return '';

    // Abreviaciones estándar del mercado financiero
    const abbreviations: { [key: string]: string } = {
      'Barrier Reverse Convertible': 'BRC',
      'Reverse Convertible': 'RC',
      'Multi Barrier Reverse Convertible': 'Multi BRC',
      'Multi Reverse Convertible': 'Multi RC',
      'Discount Certificate': 'Discount Cert',
      'Bonus Certificate': 'Bonus Cert',
      'Capital Protected': 'CP',
      'Knock Out': 'KO',
      Warrant: 'Warrant',
      Certificate: 'Cert',
      Convertible: 'Conv',
      Barrier: 'Barrier',
      Multi: 'Multi',
      Reverse: 'RC',
    };

    let abbreviatedName = name;

    // Aplicar abreviaciones en orden de longitud (más largas primero)
    const sortedKeys = Object.keys(abbreviations).sort((a, b) => b.length - a.length);

    for (const key of sortedKeys) {
      const regex = new RegExp(key, 'gi');
      abbreviatedName = abbreviatedName.replace(regex, abbreviations[key]);
    }

    return abbreviatedName;
  };

  // Calcular número de filtros activos
  const activeFiltersCount =
    selectedProductTypes.length + selectedIssuers.length + selectedReports.length;

  // Formatear fecha
  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } catch {
      return '-';
    }
  };

  const arrivedFromEvents = !!(
    normalizedInitialFilters?.eventType && normalizedInitialFilters?.eventBucket
  );

  const isLowStrikeReport = !!(
    dashboardFilters?.nearBarrier && dashboardFilters?.riskType === 'lowStrike'
  );

  const isBarrierNearReport = !!(
    dashboardFilters?.nearBarrier && dashboardFilters?.riskType === 'barrier'
  );

  const isBarrierHitReport = !!(
    dashboardFilters?.nearBarrier && dashboardFilters?.riskType === 'barrierHit'
  );

  const isRiskTableReport = isLowStrikeReport || isBarrierNearReport || isBarrierHitReport;

  const isBarrierStyleRiskPdf = isBarrierNearReport || isBarrierHitReport;

  const showDistancePctSlider = isBarrierNearReport || isLowStrikeReport;

  /** While dragging the distance slider, thumb position before commit (syncs chip + live %). */
  const [maxDistancePreview, setMaxDistancePreview] = useState<number | null>(null);

  useEffect(() => {
    if (!showDistancePctSlider) setMaxDistancePreview(null);
  }, [showDistancePctSlider]);

  const committedSliderThumb =
    dashboardFilters?.maxDistancePct != null &&
    dashboardFilters.maxDistancePct >= 1 &&
    dashboardFilters.maxDistancePct < 100
      ? dashboardFilters.maxDistancePct
      : 100;

  const sliderThumbPos =
    maxDistancePreview !== null ? maxDistancePreview : committedSliderThumb;

  const hasVisibleDashboardFilters = useMemo(() => {
    if (!dashboardFilters) return false;
    const { ownershipScope: _ignoredOwnershipScope, ...rest } = dashboardFilters;
    return Object.values(rest).some(v => v !== undefined && v !== null);
  }, [dashboardFilters]);

  const handleMaxDistancePctComplete = (value: number) => {
    const rounded = Math.round(value);
    setMaxDistancePreview(null);
    setDashboardFilters(prev => {
      const base = prev ?? {};
      return {
        ...base,
        maxDistancePct: rounded >= 100 ? undefined : rounded,
      };
    });
  };

  const riskMonitorChipLabel = (): string => {
    if (dashboardFilters?.riskType === 'barrierHit') return 'Barrier hit';
    const pos = sliderThumbPos;
    const hasCap = pos < 100;
    if (dashboardFilters?.riskType === 'lowStrike') {
      return hasCap ? `Strike monitor < ${pos}%` : 'Strike monitor';
    }
    return hasCap ? `Barrier monitor < ${pos}%` : 'Barrier monitor';
  };

  type SortKey =
    | 'name'
    | 'ccy'
    | 'issuer'
    | 'initialFixing'
    | 'finalFixing'
    | 'redemptionDate'
    | 'nextEventDate'
    | 'distancePct'
    | 'bid'
    | 'ask'
    | 'currentNotional';

  const [sortBy, setSortBy] = useState<SortKey>(() =>
    arrivedFromEvents ? 'nextEventDate' : 'finalFixing'
  );
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => 
    arrivedFromEvents ? 'asc' : 'asc'
  );

  useEffect(() => {
    if (!isRiskTableReport) return;
    setSortBy('distancePct');
    setSortDirection('asc');
  }, [isRiskTableReport]);

  const handleSort = (key: SortKey) => {
    setSortBy(prevKey => {
      if (prevKey === key) {
        setSortDirection(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      // Por defecto, texto en asc; fechas y numéricos en desc (nextEventDate en asc)
      setSortDirection(
        key === 'name' ||
        key === 'ccy' ||
        key === 'issuer' ||
        key === 'nextEventDate' ||
        key === 'distancePct'
          ? 'asc'
          : 'desc'
      );
      return key;
    });
  };

  const getDateValue = (value?: string | null) => {
    if (!value) return Number.NaN;
    const time = new Date(value).getTime();
    return isNaN(time) ? Number.NaN : time;
  };

  const getNumberValue = (value?: number | null) => {
    if (value === null || value === undefined) return Number.NaN;
    return value;
  };

  const displayProducts = useMemo(() => {
    if (!activeReport) {
      return products;
    }
    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return activeReport.products as Product[];
    }
    return activeReport.products.filter(product => {
      const name = (product.name || '').toLowerCase();
      const isin = (product.isin || '').toLowerCase();
      const valor = (product.valor || '').toLowerCase();
      return name.includes(query) || isin.includes(query) || valor.includes(query);
    }) as Product[];
  }, [activeReport, products, searchQuery]);

  const sortedProducts = useMemo(() => {
    if (!displayProducts || displayProducts.length === 0) return displayProducts;

    const sorted = [...displayProducts].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortBy) {
        case 'name':
          aVal = (a.name || '').toLowerCase();
          bVal = (b.name || '').toLowerCase();
          break;
        case 'ccy':
          aVal = (a.ccy || '').toLowerCase();
          bVal = (b.ccy || '').toLowerCase();
          break;
        case 'issuer':
          aVal = (a.issuer || '').toLowerCase();
          bVal = (b.issuer || '').toLowerCase();
          break;
        case 'initialFixing':
          aVal = getDateValue(a.initialFixing);
          bVal = getDateValue(b.initialFixing);
          break;
        case 'finalFixing':
          aVal = getDateValue(a.finalFixing);
          bVal = getDateValue(b.finalFixing);
          break;
        case 'redemptionDate':
          aVal = getDateValue(a.redemptionDate);
          bVal = getDateValue(b.redemptionDate);
          break;
        case 'bid':
          aVal = getNumberValue(a.bid);
          bVal = getNumberValue(b.bid);
          break;
        case 'nextEventDate':
          aVal = getDateValue(a.nextEventDate);
          bVal = getDateValue(b.nextEventDate);
          break;
        case 'distancePct':
          if (isBarrierStyleRiskPdf) {
            aVal = getNumberValue(a.worstBarrierUnderlyingDistancePct);
            bVal = getNumberValue(b.worstBarrierUnderlyingDistancePct);
          } else {
            aVal = getNumberValue(a.worstStrikeUnderlyingDistancePct);
            bVal = getNumberValue(b.worstStrikeUnderlyingDistancePct);
          }
          break;
        case 'ask':
          aVal = getNumberValue(a.ask);
          bVal = getNumberValue(b.ask);
          break;
        case 'currentNotional': {
          const aN = a.currentNotional ?? a.tradedNotional;
          const bN = b.currentNotional ?? b.tradedNotional;
          aVal = getNumberValue(aN ?? undefined);
          bVal = getNumberValue(bN ?? undefined);
          break;
        }
        default:
          aVal = 0;
          bVal = 0;
      }

      // Manejo de NaN / valores faltantes: siempre al final
      const aIsNaN = typeof aVal === 'number' && isNaN(aVal);
      const bIsNaN = typeof bVal === 'number' && isNaN(bVal);
      if (aIsNaN && bIsNaN) return 0;
      if (aIsNaN) return 1;
      if (bIsNaN) return -1;

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [displayProducts, sortBy, sortDirection, isBarrierStyleRiskPdf]);

  const pdfColumns = useMemo<ProductsListPdfColumn[]>(() => {
    if (isRiskTableReport) {
      if (isBarrierStyleRiskPdf) {
        return [
          { key: 'isin', label: 'ISIN' },
          { key: 'name', label: 'Product' },
          { key: 'finalFixing', label: 'Final Fixing' },
          { key: 'worstBarrierUnderlyingName', label: 'Wof Underlying' },
          { key: 'worstBarrierUnderlyingWofPct', label: 'Wof %' },
          { key: 'worstBarrierPct', label: 'Barrier %' },
          { key: 'worstBarrierUnderlyingDistancePct', label: 'Distance %' },
          { key: 'worstBarrierUnderlyingCurrent', label: 'Current' },
          { key: 'worstBarrierUnderlyingBarrier', label: 'Barrier' },
          { key: 'productTypeDisplay', label: 'Type' },
          { key: 'bid', label: 'Bid' },
        ];
      }

      return [
        { key: 'isin', label: 'ISIN' },
        { key: 'name', label: 'Product' },
        { key: 'finalFixing', label: 'Final Fixing' },
        { key: 'worstStrikeUnderlyingName', label: 'Wof Underlying' },
        { key: 'worstStrikeUnderlyingWofPct', label: 'Wof %' },
        { key: 'strikePct', label: 'Strike %' },
        { key: 'worstStrikeUnderlyingDistancePct', label: 'Distance %' },
        { key: 'worstStrikeUnderlyingCurrent', label: 'Current' },
        { key: 'worstStrikeUnderlyingStrike', label: 'Strike' },
        { key: 'bid', label: 'Bid' },
      ];
    }

    if (dashboardFilters?.eventType) {
      const columns: ProductsListPdfColumn[] = [
        { key: 'eventDate', label: 'Event Date' },
        { key: 'eventType', label: 'Event Type' },
        { key: 'isin', label: 'ISIN' },
        { key: 'valor', label: 'Valor' },
        { key: 'name', label: 'Name' },
        { key: 'worstUnderlyingName', label: 'Worst Of' },
      ];

      const normalizedEventType = (dashboardFilters.eventType || '').trim().toLowerCase();
      const hideDistanceFromAutocall =
        normalizedEventType === 'callable' ||
        normalizedEventType === 'expiry' ||
        normalizedEventType === 'coupon';

      if (!hideDistanceFromAutocall) {
        columns.push({ key: 'distanceFromAutocall', label: 'Worst Of vs Autocall' });
      }

      columns.push(
        { key: 'ccy', label: 'CCY' },
        { key: 'issuer', label: 'Issuer' },
        { key: 'bid', label: 'Bid' },
        { key: 'ask', label: 'Ask' },
        { key: 'currentNotional', label: 'Notional' },
      );

      return columns;
    }

    return [
      { key: 'isin', label: 'ISIN' },
      { key: 'valor', label: 'Valor' },
      { key: 'name', label: 'Name' },
      { key: 'ccy', label: 'CCY' },
      { key: 'issuer', label: 'Issuer' },
      { key: 'initialFixing', label: 'Initial Fixing' },
      { key: 'finalFixing', label: 'Final Fixing' },
      { key: 'redemptionDate', label: 'Redemption Date' },
      { key: 'bid', label: 'Bid' },
      { key: 'ask', label: 'Ask' },
      { key: 'currentNotional', label: 'Notional' },
    ];
  }, [dashboardFilters?.eventType, isBarrierStyleRiskPdf, isRiskTableReport]);

  const exportTitle = useMemo(() => {
    if (activeReport?.name) {
      return activeReport.name;
    }
    if (isBarrierHitReport) {
      return 'Barrier hit';
    }
    if (isBarrierNearReport) {
      const pos = sliderThumbPos;
      const hasCap = pos < 100;
      return hasCap ? `Barrier monitor < ${pos}%` : 'Barrier monitor';
    }
    if (isLowStrikeReport) {
      const pos = sliderThumbPos;
      const hasCap = pos < 100;
      return hasCap ? `Strike monitor < ${pos}%` : 'Strike monitor';
    }
    if (dashboardFilters?.eventDate) {
      return `Events - ${formatCalendarChipDate(dashboardFilters.eventDate)}`;
    }
    if (dashboardFilters?.eventType) {
      const eventLabel = dashboardFilters.eventType.trim();
      const periodLabel =
        dashboardFilters?.eventBucket === 'nextWeek'
          ? 'Next Week'
          : dashboardFilters?.eventBucket === 'nextMaturity'
            ? 'Next Month'
            : 'Filtered';
      return `${eventLabel} - ${periodLabel}`;
    }
    return 'Lifecycle Report';
  }, [
    activeReport?.name,
    dashboardFilters?.eventBucket,
    dashboardFilters?.eventDate,
    dashboardFilters?.eventType,
    isBarrierHitReport,
    isBarrierNearReport,
    isLowStrikeReport,
    sliderThumbPos,
  ]);

  useEffect(() => {
    const reportId = selectedReports[0];
    if (!reportId) {
      setActiveReport(null);
      return;
    }

    let cancelled = false;
    apiService
      .getCustomReport(reportId)
      .then(report => {
        if (!cancelled) {
          setActiveReport(report);
        }
      })
      .catch(err => {
        console.error('Error fetching custom report:', err);
        if (!cancelled) {
          setActiveReport(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedReports]);

  const fetchAssignedPortfolioProducts = async (): Promise<Product[]> => {
    const productMap = new Map<number, Product>();
    let currentPage = 1;
    let hasNext = true;
    let safetyMaxPages = 10_000;
    const pageSize = 100;

    while (hasNext && currentPage <= safetyMaxPages) {
      const response = await apiService.getProductsList(currentPage, pageSize, {
        ownershipScope,
      });
      (response.data || []).forEach(product => productMap.set(product.PROId, product));
      hasNext = response.pagination?.hasNextPage || false;
      const totalPages = response.pagination?.totalPages;
      if (typeof totalPages === 'number' && totalPages > 0) {
        safetyMaxPages = Math.min(safetyMaxPages, totalPages);
      }
      currentPage += 1;
    }

    return Array.from(productMap.values());
  };

  const loadReportModalData = async (
    mode: 'create' | 'edit',
    report?: CustomReportDetail | null
  ) => {
    const requestId = reportModalRequestIdRef.current + 1;
    reportModalRequestIdRef.current = requestId;

    setReportModalMode(mode);
    setReportError(null);
    setReportModalVisible(true);
    setReportModalProducts([]);
    setLoadingColleagues(true);
    setLoadingReportProducts(true);

    try {
      const [list, portfolio] = await Promise.all([
        apiService.getCustomReportColleagues(),
        fetchAssignedPortfolioProducts(),
      ]);

      if (requestId !== reportModalRequestIdRef.current) {
        return;
      }

      setColleagues(list);

      const assigned = portfolio.filter(
        product => (product.currentNotional ?? product.tradedNotional ?? 0) > 0
      );

      if (mode === 'edit' && report) {
        const selectedById = new Map(
          report.products.map(product => [product.PROId, product])
        );
        const drafts: ReportProductDraft[] = [];
        const seen = new Set<number>();

        for (const product of assigned) {
          const existing = selectedById.get(product.PROId);
          const maxNotional = Number(product.currentNotional ?? product.tradedNotional ?? 0);
          drafts.push({
            productId: product.PROId,
            name: product.name,
            isin: product.isin,
            ccy: product.ccy,
            maxNotional,
            allocatedNotional: String(
              Math.round(existing ? Number(existing.allocatedNotional) : maxNotional)
            ),
            selected: Boolean(existing),
          });
          seen.add(product.PROId);
        }

        for (const product of report.products) {
          if (seen.has(product.PROId)) {
            continue;
          }
          drafts.push({
            productId: product.PROId,
            name: product.name,
            isin: product.isin,
            ccy: product.ccy,
            maxNotional: Number(product.maxNotional ?? product.allocatedNotional ?? 0),
            allocatedNotional: String(Math.round(Number(product.allocatedNotional ?? 0))),
            selected: true,
          });
        }

        setReportModalProducts(drafts);
        return;
      }

      setReportModalProducts(
        assigned.map(product => {
          const maxNotional = Number(product.currentNotional ?? product.tradedNotional ?? 0);
          return {
            productId: product.PROId,
            name: product.name,
            isin: product.isin,
            ccy: product.ccy,
            allocatedNotional: String(Math.round(maxNotional)),
            maxNotional,
            selected: false,
          };
        })
      );
    } catch (err) {
      console.error('Error loading report modal data:', err);
      if (requestId !== reportModalRequestIdRef.current) {
        return;
      }
      setColleagues([]);
      setReportModalProducts([]);
    } finally {
      if (requestId === reportModalRequestIdRef.current) {
        setLoadingColleagues(false);
        setLoadingReportProducts(false);
      }
    }
  };

  const openCreateReportModal = async () => {
    setReportModalInitial(null);
    await loadReportModalData('create');
  };

  const openEditReportModal = async () => {
    if (!activeReport?.isOwner) return;
    setReportModalInitial(activeReport);
    await loadReportModalData('edit', activeReport);
  };

  const handleSubmitReport = async (input: CreateReportSubmitInput) => {
    try {
      setSubmittingReport(true);
      setReportError(null);

      if (reportModalMode === 'create') {
        if (input.products.length === 0) {
          setReportError('Add at least one product with notional.');
          return;
        }

        const created = await apiService.createCustomReport({
          name: input.name,
          memberIds: input.memberIds,
          products: input.products,
        });
        await reloadReports();
        setSelectedReports([created.id]);
        setActiveReport(created);
      } else if (activeReport) {
        const updated = await apiService.updateCustomReport(activeReport.id, {
          name: input.name,
          memberIds: input.memberIds,
          products: input.products,
        });
        await reloadReports();
        setActiveReport(updated);
      }

      setReportModalVisible(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not save the report';
      setReportError(message);
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!activeReport) return;

    const confirmed =
      Platform.OS === 'web'
        ? window.confirm(`Delete portfolio "${activeReport.name}"?`)
        : await new Promise<boolean>(resolve => {
            Alert.alert('Delete portfolio', `Delete "${activeReport.name}"?`, [
              { text: 'Cancel', style: 'cancel', onPress: () => resolve(false) },
              { text: 'Delete', style: 'destructive', onPress: () => resolve(true) },
            ]);
          });

    if (!confirmed) return;

    try {
      setSubmittingReport(true);
      await apiService.deleteCustomReport(activeReport.id);
      setReportModalVisible(false);
      setSelectedReports([]);
      setActiveReport(null);
      await reloadReports();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not delete the report';
      setReportError(message);
    } finally {
      setSubmittingReport(false);
    }
  };

  const handleExportPdf = async () => {
    if (isExportingPdf || sortedProducts.length === 0 || pdfColumns.length === 0) return;
    try {
      setIsExportingPdf(true);
      const sessionUser = authService.getCurrentUser() as { companyName?: string | null } | null;
      const exporterCompanyName =
        sessionUser && typeof sessionUser.companyName === 'string' ? sessionUser.companyName : '';
      await exportProductsListPdf({
        products: sortedProducts,
        columns: pdfColumns,
        eventType: dashboardFilters?.eventType,
        eventBucket: dashboardFilters?.eventBucket,
        title: exportTitle,
        exporterCompanyName,
        fetchProductDetail: isin => apiService.getProductByIsin(isin, ownershipScope),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Could not export the report';
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        window.alert(message);
      } else {
        Alert.alert('Export report', message);
      }
    } finally {
      setIsExportingPdf(false);
    }
  };

  const renderProductItem = ({ item }: { item: Product }) => {
    // Determine if we should show event-related columns based on filters
    const isEventsFilter = !!dashboardFilters?.eventType;
    const normalizedEventType = (dashboardFilters?.eventType || '').trim().toLowerCase();
    const hideDistanceFromAutocall =
      normalizedEventType === 'callable' ||
      normalizedEventType === 'expiry' ||
      normalizedEventType === 'coupon';

    // showEventColumns: layout with Worst Of / Distance columns (used for both events and expiry)
    const showEventColumns = isEventsFilter;
    // showEventDateTypeColumns: only for real events (Autocall / Coupon / explicit eventType)
    const showEventDateTypeColumns = isEventsFilter;

    return (
      <ProductRow
        isin={item.isin}
        valor={item.valor}
        name={item.name}
        bid={item.bid}
        ask={item.ask}
        ccy={item.ccy}
        issuer={item.issuer}
        initialFixing={item.initialFixing}
        finalFixing={item.finalFixing}
        redemptionDate={item.redemptionDate}
        tradedNotional={item.tradedNotional ?? undefined}
        isRiskTableLayout={isRiskTableReport}
        riskReportKind={
          isBarrierStyleRiskPdf ? 'barrier' : isLowStrikeReport ? 'lowStrike' : undefined
        }
        strikePct={item.strikePct}
        worstStrikeUnderlyingName={item.worstStrikeUnderlyingName}
        worstStrikeUnderlyingWofPct={item.worstStrikeUnderlyingWofPct}
        worstStrikeUnderlyingDistancePct={item.worstStrikeUnderlyingDistancePct}
        worstStrikeUnderlyingCurrent={item.worstStrikeUnderlyingCurrent}
        worstStrikeUnderlyingStrike={item.worstStrikeUnderlyingStrike}
        worstBarrierUnderlyingName={item.worstBarrierUnderlyingName}
        worstBarrierUnderlyingWofPct={item.worstBarrierUnderlyingWofPct}
        worstBarrierPct={item.worstBarrierPct}
        worstBarrierUnderlyingDistancePct={item.worstBarrierUnderlyingDistancePct}
        worstBarrierUnderlyingCurrent={item.worstBarrierUnderlyingCurrent}
        worstBarrierUnderlyingBarrier={item.worstBarrierUnderlyingBarrier}
        barrierStyleName={item.barrierStyleName}
        productTypeDisplay={
          item.productTypeDisplay ??
          (item.productCode ? getProductTypeName(item.productCode) : undefined)
        }
        productCode={item.productCode}
        currentNotional={item.currentNotional}
        showEventColumns={showEventColumns}
        showDistanceFromAutocallColumn={!hideDistanceFromAutocall}
        showEventDateTypeColumns={showEventDateTypeColumns}
        nextEventDate={item.nextEventDate}
        eventType={item.eventType ?? undefined}
        worstUnderlyingName={item.worstUnderlyingName}
        distanceFromAutocall={item.distanceFromAutocall ?? undefined}
        ownershipScope={ownershipScope}
        onPress={() => onProductPress && onProductPress(item.isin, Boolean(activeReport))}
        abbreviateName={abbreviateProductName}
        formatPrice={formatPrice}
        formatDate={formatDate}
      />
    );
  };

  // Renderizar footer con botón de cargar más
  // Renderizar mensaje vacío
  const renderEmpty = () => {
    if (loading && !activeReport) {
      return <SkeletonProductList />;
    }

    if (error) {
      return (
        <View style={styles.emptyContainer}>
          <Text style={styles.errorText}>❌ {error}</Text>
          <TouchableOpacity onPress={handleRefresh} style={styles.retryButton}>
            <Text style={styles.retryText}>Reintentar</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>No products found</Text>
      </View>
    );
  };

  return (
    <ScreenTransition transitionType='slideFromRight'>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          {width < 768 && (
            <TouchableOpacity onPress={onBackPress} style={styles.backButton}>
              <Text style={styles.backIcon}>‹</Text>
            </TouchableOpacity>
          )}
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={styles.headerTitle}>Portfolio</Text>
            {showOwnershipScopeToggle && (
              <OwnershipScopeToggle
                ownershipScope={ownershipScope}
                onOwnershipScopeChange={setOwnershipScope}
              />
            )}
          </View>
          <View style={styles.headerSpacer} />
        </View>

        {/* Search Bar with Filters Button */}
        <View style={styles.searchAndFiltersContainer}>
          <View style={styles.searchContainer}>
            <Text style={styles.searchIcon}>🔍</Text>
            <TextInput
              style={styles.searchInput}
              placeholder='Product name, ISIN, Valor, etc...'
              placeholderTextColor='#9CA3AF'
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
          </View>
          <TouchableOpacity
            style={styles.filtersButton}
            onPress={() => setFiltersModalVisible(true)}
            activeOpacity={0.7}
          >
            <Text style={styles.filtersButtonText}>Filters</Text>
            {activeFiltersCount > 0 && (
              <View style={styles.filtersBadge}>
                <Text style={styles.filtersBadgeText}>{activeFiltersCount}</Text>
              </View>
            )}
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.createReportButton}
            onPress={openCreateReportModal}
            activeOpacity={0.7}
          >
            <Text style={styles.createReportButtonText}>Create portfolio</Text>
          </TouchableOpacity>
          {activeReport?.isOwner && (
            <TouchableOpacity
              style={styles.editReportButton}
              onPress={openEditReportModal}
              activeOpacity={0.7}
            >
              <Text style={styles.editReportButtonText}>Edit portfolio</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.exportPdfButton, (isExportingPdf || sortedProducts.length === 0) && styles.exportPdfButtonDisabled]}
            onPress={handleExportPdf}
            activeOpacity={0.7}
            disabled={isExportingPdf || sortedProducts.length === 0}
          >
            <Text style={styles.exportPdfButtonText}>
              {isExportingPdf ? 'Exporting...' : 'Export report'}
            </Text>
          </TouchableOpacity>
        </View>
        {/* Filters Modal */}
        <FiltersModal
          visible={filtersModalVisible}
          onClose={() => setFiltersModalVisible(false)}
          productTypes={productTypes}
          issuers={issuers}
          reports={reports}
          selectedProductTypes={selectedProductTypes}
          selectedIssuers={selectedIssuers}
          selectedReports={selectedReports}
          onProductTypesChange={setSelectedProductTypes}
          onIssuersChange={setSelectedIssuers}
          onReportsChange={values => setSelectedReports(values.slice(0, 1))}
          loading={loadingFilterOptions}
        />
        <CreateReportModal
          visible={reportModalVisible}
          mode={reportModalMode}
          colleagues={colleagues}
          initialReport={reportModalMode === 'edit' ? reportModalInitial : null}
          initialProducts={reportModalProducts}
          loadingColleagues={loadingColleagues}
          loadingProducts={loadingReportProducts}
          submitting={submittingReport}
          errorMessage={reportError}
          onClose={() => setReportModalVisible(false)}
          onSubmit={handleSubmitReport}
          onDelete={reportModalMode === 'edit' ? handleDeleteReport : undefined}
        />

        {/* Active Filters Display */}
        {(hasVisibleDashboardFilters ||
          selectedProductTypes.length > 0 ||
          selectedIssuers.length > 0 ||
          selectedReports.length > 0) && (
          <View style={styles.activeFiltersContainer}>
            <Text style={styles.activeFiltersTitle}>Active Filters:</Text>
            <View style={styles.activeFiltersRow}>
              {dashboardFilters?.currency && (
                <View style={styles.filterChip}>
                  <Text style={styles.filterChipText}>Currency: {dashboardFilters.currency}</Text>
                  <TouchableOpacity
                    onPress={() =>
                      setDashboardFilters(prev => (prev ? { ...prev, currency: undefined } : null))
                    }
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              )}
              {/* Mostrar chips individuales para cada código seleccionado */}
              {selectedProductTypes.map(code => (
                <View key={code} style={styles.filterChip}>
                  <Text style={styles.filterChipText}>{getProductTypeName(code)}</Text>
                  <TouchableOpacity
                    onPress={() =>
                      setSelectedProductTypes(selectedProductTypes.filter(c => c !== code))
                    }
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              ))}
              {/* Mostrar chips individuales para cada issuer seleccionado */}
              {selectedIssuers.map(issuer => (
                <View key={issuer} style={styles.filterChip}>
                  <Text style={styles.filterChipText}>Issuer: {issuer}</Text>
                  <TouchableOpacity
                    onPress={() => setSelectedIssuers(selectedIssuers.filter(i => i !== issuer))}
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              ))}
              {selectedReports.map(reportId => {
                const reportLabel =
                  reports.find(opt => opt.value === reportId)?.label || reportId;
                return (
                  <View key={reportId} style={styles.filterChip}>
                    <Text style={styles.filterChipText}>Portfolio: {reportLabel}</Text>
                    <TouchableOpacity
                      onPress={() =>
                        setSelectedReports(selectedReports.filter(id => id !== reportId))
                      }
                      style={styles.filterChipClose}
                    >
                      <Text style={styles.filterChipCloseText}>×</Text>
                    </TouchableOpacity>
                  </View>
                );
              })}
              {/* Mantener el chip del issuer legacy solo si no está en selectedIssuers (para compatibilidad) */}
              {dashboardFilters?.issuer && !selectedIssuers.includes(dashboardFilters.issuer) && (
                <View style={styles.filterChip}>
                  <Text style={styles.filterChipText}>Issuer: {dashboardFilters.issuer}</Text>
                  <TouchableOpacity
                    onPress={() => {
                      setDashboardFilters(prev => {
                        if (prev) {
                          const { issuer, ...rest } = prev;
                          return Object.keys(rest).length > 0 ? rest : null;
                        }
                        return null;
                      });
                    }}
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              )}
              {dashboardFilters?.maturityQuarter && (
                <View style={styles.filterChip}>
                  <Text style={styles.filterChipText}>
                    Quarter: {dashboardFilters.maturityQuarter}
                  </Text>
                  <TouchableOpacity
                    onPress={() =>
                      setDashboardFilters(prev =>
                        prev ? { ...prev, maturityQuarter: undefined } : null
                      )
                    }
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              )}
              {dashboardFilters?.underlying && (
                <View style={styles.filterChip}>
                  <Text style={styles.filterChipText}>
                    Underlying: {dashboardFilters.underlying}
                  </Text>
                  <TouchableOpacity
                    onPress={() =>
                      setDashboardFilters(prev =>
                        prev ? { ...prev, underlying: undefined } : null
                      )
                    }
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              )}
              {dashboardFilters?.eventDate ? (
                <View style={styles.filterChip}>
                  <Text style={styles.filterChipText}>
                    Events: {formatCalendarChipDate(dashboardFilters.eventDate)}
                  </Text>
                  <TouchableOpacity
                    onPress={() =>
                      setDashboardFilters(prev =>
                        prev ? { ...prev, eventType: undefined, eventDate: undefined } : null
                      )
                    }
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              ) : dashboardFilters?.eventType ? (
                <View style={styles.filterChip}>
                  <Text style={styles.filterChipText}>Event: {dashboardFilters.eventType}</Text>
                  <TouchableOpacity
                    onPress={() =>
                      setDashboardFilters(prev => (prev ? { ...prev, eventType: undefined } : null))
                    }
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              ) : null}
              {dashboardFilters?.nearBarrier && (
                <View style={styles.filterChip}>
                  <Text style={styles.filterChipText}>{riskMonitorChipLabel()}</Text>
                  <TouchableOpacity
                    onPress={() =>
                      setDashboardFilters(prev =>
                        prev
                          ? {
                              ...prev,
                              nearBarrier: undefined,
                              riskType: undefined,
                              maxDistancePct: undefined,
                            }
                          : null
                      )
                    }
                    style={styles.filterChipClose}
                  >
                    <Text style={styles.filterChipCloseText}>×</Text>
                  </TouchableOpacity>
                </View>
              )}
              <TouchableOpacity
                onPress={() => {
                  setDashboardFilters(null);
                  setSelectedProductTypes([]);
                  setSelectedIssuers([]);
                  setSelectedReports([]);
                  setOwnershipScope('group');
                }}
                style={styles.clearAllButton}
              >
                <Text style={styles.clearAllText}>Clear All</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        {showDistancePctSlider && (
          <View style={styles.riskDistanceFilterSection}>
            <Text style={styles.riskDistanceFilterSubtitle}>Less than</Text>
            <View style={styles.riskDistanceSliderRow}>
              <Slider
                style={styles.riskDistanceSlider}
                minimumValue={1}
                maximumValue={100}
                step={1}
                value={sliderThumbPos}
                onValueChange={(v: number) => setMaxDistancePreview(Math.round(v))}
                onSlidingComplete={handleMaxDistancePctComplete}
                minimumTrackTintColor={COLORS.primary}
                maximumTrackTintColor="#E5E7EB"
                thumbTintColor={COLORS.primary}
              />
              <Text style={styles.riskDistanceFilterLiveValue}>{sliderThumbPos}%</Text>
            </View>
          </View>
        )}

        {/* Table Card Container */}
        <View style={width >= 768 ? styles.tableCard : styles.tableCardMobile}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            {width >= 1024 && isRiskTableReport ? (
              isBarrierStyleRiskPdf ? (
                <>
                  <Text style={[styles.headerCell, styles.favouriteHeader]} />
                  <Text style={[styles.headerCell, styles.isinHeader]}>ISIN</Text>
                  <Text style={[styles.headerCell, styles.nameHeader]}>Product</Text>
                  <Text style={[styles.headerCell, styles.finalFixingHeader]}>Final Fixing</Text>
                  <Text style={[styles.headerCell, styles.wofUnderlyingHeader]}>Wof Underlying</Text>
                  <Text style={[styles.headerCell, styles.wofPctHeader]}>Wof %</Text>
                  <Text style={[styles.headerCell, styles.strikePctHeader]}>Barrier %</Text>
                  <TouchableOpacity
                    style={styles.distancePctHeader}
                    onPress={() => handleSort('distancePct')}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.headerCell, styles.distancePctHeader]}>
                      Distance %
                      {sortBy === 'distancePct' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                    </Text>
                  </TouchableOpacity>
                  <Text style={[styles.headerCell, styles.currentHeader]}>Current</Text>
                  <Text style={[styles.headerCell, styles.strikeHeader]}>Barrier</Text>
                  <Text style={[styles.headerCell, styles.riskTableTypeHeader]}>Type</Text>
                  <Text style={[styles.headerCell, styles.bidHeader]}>Bid</Text>
                </>
              ) : (
                <>
                  <Text style={[styles.headerCell, styles.favouriteHeader]} />
                  <Text style={[styles.headerCell, styles.isinHeader]}>ISIN</Text>
                  <Text style={[styles.headerCell, styles.nameHeader]}>Product</Text>
                  <Text style={[styles.headerCell, styles.finalFixingHeader]}>Final Fixing</Text>
                  <Text style={[styles.headerCell, styles.wofUnderlyingHeader]}>Wof Underlying</Text>
                  <Text style={[styles.headerCell, styles.wofPctHeader]}>Wof %</Text>
                  <Text style={[styles.headerCell, styles.strikePctHeader]}>Strike %</Text>
                  <TouchableOpacity
                    style={styles.distancePctHeader}
                    onPress={() => handleSort('distancePct')}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.headerCell, styles.distancePctHeader]}>
                      Distance %
                      {sortBy === 'distancePct' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                    </Text>
                  </TouchableOpacity>
                  <Text style={[styles.headerCell, styles.currentHeader]}>Current</Text>
                  <Text style={[styles.headerCell, styles.strikeHeader]}>Strike</Text>
                  <Text style={[styles.headerCell, styles.bidHeader]}>Bid</Text>
                </>
              )
            ) : (
              <>
                <Text style={[styles.headerCell, styles.favouriteHeader]} />
                {width >= 1024 && dashboardFilters?.eventType && (
                  <>
                    <Text style={[styles.headerCell, styles.eventDateHeader]}>Event Date</Text>
                    <Text style={[styles.headerCell, styles.eventTypeHeader]}>Event Type</Text>
                  </>
                )}

                <Text style={[styles.headerCell, styles.isinHeader]}>ISIN</Text>
                {width >= 1024 && <Text style={[styles.headerCell, styles.valorHeader]}>Valor</Text>}
                <TouchableOpacity
                  style={styles.nameHeader}
                  onPress={() => handleSort('name')}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.headerCell, styles.nameHeader]}>
                    Name
                    {sortBy === 'name' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                  </Text>
                </TouchableOpacity>

                {width >= 1024 && dashboardFilters?.eventType && (
                  <>
                    <Text style={[styles.headerCell, styles.worstOfHeader]}>Worst Of</Text>
                    {!['callable', 'expiry', 'coupon'].includes(
                      (dashboardFilters.eventType || '').trim().toLowerCase()
                    ) && (
                      <Text style={[styles.headerCell, styles.distanceHeader]}>Worst Of vs Autocall</Text>
                    )}
                  </>
                )}

                <TouchableOpacity
                  style={styles.ccyHeader}
                  onPress={() => handleSort('ccy')}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.headerCell, styles.ccyHeader]}>
                    CCY
                    {sortBy === 'ccy' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                  </Text>
                </TouchableOpacity>
                {width >= 1024 && (
                  <TouchableOpacity
                    style={styles.issuerHeader}
                    onPress={() => handleSort('issuer')}
                    activeOpacity={0.7}
                  >
                    <Text style={[styles.headerCell, styles.issuerHeader]}>
                      Issuer
                      {sortBy === 'issuer' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                    </Text>
                  </TouchableOpacity>
                )}

                {width >= 1024 && !dashboardFilters?.eventType && (
                  <>
                    <TouchableOpacity
                      style={styles.fixingHeader}
                      onPress={() => handleSort('initialFixing')}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.headerCell, styles.fixingHeader]}>
                        Initial Fixing
                        {sortBy === 'initialFixing' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.fixingHeader}
                      onPress={() => handleSort('finalFixing')}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.headerCell, styles.fixingHeader]}>
                        Final Fixing
                        {sortBy === 'finalFixing' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                      </Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.dateHeader}
                      onPress={() => handleSort('redemptionDate')}
                      activeOpacity={0.7}
                    >
                      <Text style={[styles.headerCell, styles.dateHeader]}>
                        Redemption Date
                        {sortBy === 'redemptionDate' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                      </Text>
                    </TouchableOpacity>
                  </>
                )}

                <TouchableOpacity
                  style={styles.bidHeader}
                  onPress={() => handleSort('bid')}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.headerCell, styles.bidHeader]}>
                    Bid
                    {sortBy === 'bid' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.askHeader}
                  onPress={() => handleSort('ask')}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.headerCell, styles.askHeader]}>
                    Ask
                    {sortBy === 'ask' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                  </Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.fixingHeader}
                  onPress={() => handleSort('currentNotional')}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.headerCell, styles.fixingHeader]}>
                    Notional
                    {sortBy === 'currentNotional' && (sortDirection === 'asc' ? ' ▴' : ' ▾')}
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>

          {/* Products List with Infinite Scroll */}
          <View style={styles.listContainer}>
              <FlatList
              data={sortedProducts}
              renderItem={renderProductItem}
              keyExtractor={(item, index) => `${item.PROId}-${index}`}
              style={styles.productsList}
              contentContainerStyle={products.length === 0 ? styles.emptyListContent : undefined}
              showsVerticalScrollIndicator={false}
              refreshing={refreshing}
              onRefresh={handleRefresh}
              progressViewOffset={0}
              ListFooterComponent={null}
              ListEmptyComponent={renderEmpty}
              removeClippedSubviews={true}
              maxToRenderPerBatch={10}
              updateCellsBatchingPeriod={50}
              initialNumToRender={15}
              windowSize={10}
              getItemLayout={undefined}
            />

            {/* Loading skeleton for initial load */}
            {loading && products.length === 0 && (
              <View style={styles.skeletonContainer}>
                <SkeletonProductList />
              </View>
            )}
          </View>
        </View>
      </View>
    </ScreenTransition>
  );
};
